import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import StoreContext from '../../Store/context';
import { getInvoiceUrlForCompany } from '../../../services/subscriptionService';

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 16,
        padding: theme.spacing(2),
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.error.main,
        fontWeight: 'bold',
    },
    icon: {
        fontSize: '2.5rem',
    },
    highlight: {
        fontWeight: 'bold',
        color: theme.palette.text.primary,
    },
    actionButton: {
        borderRadius: 8,
        fontWeight: 'bold',
        padding: theme.spacing(1, 4),
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
}));

const LicenseExpiredModal = ({ open, onClose, licenseEndTime }) => {
    const classes = useStyles();
    const history = useHistory();
    const { credencials } = useContext(StoreContext);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [fetchedInvoiceUrl, setFetchedInvoiceUrl] = useState(null);

    // Check if logged-in user is admin
    const profileId = credencials?.profile?.id;
    const profileName = credencials?.profile?.name || '';
    const isAdmin =
        profileId === 1 ||
        profileId === '1' ||
        profileName.toLowerCase().includes('admin');

    const companyId = credencials?.company || credencials?.companyData?.id;
    const sessionToken = credencials?.sessionToken;

    useEffect(() => {
        let isMounted = true;
        if (open && isAdmin && companyId) {
            getInvoiceUrlForCompany(companyId, sessionToken).then((url) => {
                if (isMounted && url) {
                    setFetchedInvoiceUrl(url);
                }
            });
        }
        return () => {
            isMounted = false;
        };
    }, [open, isAdmin, companyId, sessionToken]);

    const handleRedirect = async () => {
        if (fetchedInvoiceUrl) {
            window.open(fetchedInvoiceUrl, '_blank');
            if (onClose) onClose();
            return;
        }

        // Open blank tab synchronously within user click event to prevent browser popup blocking
        const newWindow = window.open('about:blank', '_blank');

        setLoadingInvoice(true);
        try {
            const url = await getInvoiceUrlForCompany(companyId, sessionToken);
            if (url) {
                if (newWindow && !newWindow.closed) {
                    newWindow.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
            } else {
                if (newWindow && !newWindow.closed) {
                    newWindow.close();
                }
                history.push('/home/gestao/empresa');
            }
            if (onClose) onClose();
        } catch (err) {
            console.error('Error getting invoice URL:', err);
            if (newWindow && !newWindow.closed) {
                newWindow.close();
            }
            history.push('/home/gestao/empresa');
            if (onClose) onClose();
        } finally {
            setLoadingInvoice(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.paper }}
        >
            <DialogTitle>
                <Box className={classes.title}>
                    <WarningIcon className={classes.icon} />
                    <Typography variant="h5" component="span" style={{ fontWeight: 'bold' }}>
                        Licença Expirada
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box mb={3}>
                    <Typography variant="body1" gutterBottom>
                        Sua licença de uso do sistema expirou.
                    </Typography>

                    <Box mt={2} p={2} bgcolor="#fff5f5" borderRadius={8} border="1px solid #feb2b2">
                        <Typography variant="body2" color="error" style={{ fontWeight: 'bold' }}>
                            <EventBusyIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            Atenção aos prazos de segurança e consulta:
                        </Typography>
                        <Box mt={1} pl={4}>
                            <Typography variant="body2" gutterBottom>
                                • <strong>Modo Imprevisto:</strong> Você tem acesso completo ao sistema por até <strong>2 dias</strong> após o vencimento.
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                • <strong>Modo Consulta:</strong> Você tem acesso apenas para visualização de dados por <strong>14 dias</strong> após o vencimento.
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                • <strong>Bloqueio de Login:</strong> Após <strong>30 dias</strong> do vencimento, o acesso ao sistema será completamente desabilitado.
                            </Typography>
                        </Box>
                    </Box>

                    <Box mt={3}>
                        <Typography variant="body2" color="textSecondary">
                            Para continuar utilizando todas as funcionalidades e evitar o bloqueio total, realize a renovação agora mesmo.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions style={{ padding: '16px 24px' }}>
                <Button onClick={onClose} color="default" disabled={loadingInvoice}>
                    FECHAR
                </Button>
                <Button
                    onClick={handleRedirect}
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                    disabled={loadingInvoice}
                    startIcon={
                        loadingInvoice ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <EventBusyIcon />
                        )
                    }
                >
                    {loadingInvoice ? 'BUSCANDO FATURA...' : 'RENOVAR LICENÇA'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LicenseExpiredModal;
